import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "card custom-shadow rounded-3 bg-white border mb-4" }
const _hoisted_2 = { class: "card-body p-4" }
const _hoisted_3 = { class: "d-flex justify-content-between align-items-center flex-wrap gap-1 mb-3" }
const _hoisted_4 = ["innerHTML"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = ["innerHTML"]
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = { style: {"margin-bottom":"-15px","margin-left":"-10px","margin-top":"-5px"} }
const _hoisted_9 = ["innerHTML"]

import { ref, computed, onMounted } from 'vue';
import { t } from '@/utils/i18nHelper';


export default /*@__PURE__*/_defineComponent({
  __name: 'MonthlyFlowChart',
  props: {
  schedule: {
    type: Object,
    required: true,
  },
},
  setup(__props) {

const props = __props;

const isClient = ref(false);
const selectedTimeFrame = ref('Monthly');

onMounted(() => {
  isClient.value = true;
});

// Uso diretto della funzione t() nel template

// Helper to get the week number (ISO 8601)
const getWeek = (d: Date) => {
  d = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${weekNo}`;
};

const chartData = computed(() => {
  const events = props.schedule?.events || [];
  let totals: { [key: string]: { versamenti: number; pagamenti: number } } = {};
  let keyFn: (date: Date) => string;
  let categoryLabelFn: (key: string) => string;

  switch (selectedTimeFrame.value) {
    case 'Weekly':
      keyFn = (date) => getWeek(date);
      categoryLabelFn = (key) => key;
      break;
    case 'Yearly':
      keyFn = (date) => date.getFullYear().toString();
      categoryLabelFn = (key) => key;
      break;
    default: // Monthly
      keyFn = (date) => date.toISOString().slice(0, 7); // YYYY-MM
      categoryLabelFn = (key) => {
        const [year, month] = key.split('-');
        return new Date(parseInt(year), parseInt(month) - 1).toLocaleString('it-IT', { month: 'short', year: 'numeric' });
      };
      break;
  }

  events.forEach((event: any) => {
    const key = keyFn(new Date(event.date));
    if (!totals[key]) {
      totals[key] = { versamenti: 0, pagamenti: 0 };
    }
    if (event.isIncome) {
      totals[key].versamenti += event.amount;
    } else {
      totals[key].pagamenti += Math.abs(event.amount);
    }
  });

  const sortedKeys = Object.keys(totals).sort();
  const categories = sortedKeys.map(categoryLabelFn);
  const versamentiData = sortedKeys.map(key => parseFloat(totals[key].versamenti.toFixed(2)));
  const pagamentiData = sortedKeys.map(key => parseFloat(totals[key].pagamenti.toFixed(2)));

  return {
    series: [
      { name: 'Con Smart Rate: pagamenti distribuiti e sostenibili', data: versamentiData },
      { name: 'Senza pianificazione: picchi fiscali difficili da gestire', data: pagamentiData },
    ],
    categories: categories,
  };
});

const chartOptions = computed(() => ({
  chart: {
    type: 'area',
    height: 366,
    zoom: { enabled: false },
    toolbar: { show: false }
  },
  colors: ['#6a7cf9', '#fd5812'],
  dataLabels: { enabled: false },
  stroke: { curve: 'smooth', width: [2, 2], dashArray: [0, 5] },
  grid: { borderColor: '#f1f1f1' },
  fill: {
    type: 'gradient',
    gradient: {
      stops: [0, 90, 100],
      shadeIntensity: 1,
      opacityFrom: 0.2,
      opacityTo: 0.7,
    },
  },
  xaxis: {
    categories: chartData.value.categories,
    axisTicks: { show: false },
    axisBorder: { show: false },
    labels: { style: { colors: '#8695AA', fontSize: '12px' } },
  },
  yaxis: {
    tickAmount: 5,
    labels: {
      formatter: (val: number) => '€' + val.toLocaleString('it-IT'),
      style: { colors: '#8695AA', fontSize: '12px' },
    },
  },
  legend: {
    show: true,
    position: 'top',
    fontSize: '12px',
    horizontalAlign: 'left',
    itemMargin: { horizontal: 8, vertical: 0 },
    labels: { colors: '#64748B' },
    markers: { width: 9, height: 9, offsetX: -2, offsetY: -0.5, radius: 2 },
  },
  tooltip: {
    y: {
      formatter: (val: number) => '€' + val.toLocaleString('it-IT'),
    },
  },
}));


return (_ctx: any,_cache: any) => {
  const _component_apexchart = _resolveComponent("apexchart")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("h4", {
          class: "mb-0 fw-semibold",
          innerHTML: _unref(t)('fr.monthly_flow.chart_title', 'Con e senza Smartrate')
        }, null, 8, _hoisted_4),
        _withDirectives(_createElementVNode("select", {
          class: "form-select month-select form-control",
          "aria-label": "Default select example",
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedTimeFrame).value = $event))
        }, [
          _createElementVNode("option", {
            value: "Monthly",
            innerHTML: _unref(t)('fr.monthly_flow.timeframe_monthly', 'Mensile')
          }, null, 8, _hoisted_5),
          _createElementVNode("option", {
            value: "Weekly",
            innerHTML: _unref(t)('fr.monthly_flow.timeframe_weekly', 'Settimanale')
          }, null, 8, _hoisted_6),
          _createElementVNode("option", {
            value: "Yearly",
            innerHTML: _unref(t)('fr.monthly_flow.timeframe_yearly', 'Annuale')
          }, null, 8, _hoisted_7)
        ], 512), [
          [_vModelSelect, selectedTimeFrame.value]
        ])
      ]),
      _createElementVNode("div", _hoisted_8, [
        (isClient.value)
          ? (_openBlock(), _createBlock(_component_apexchart, {
              key: 0,
              type: "area",
              height: "365",
              options: chartOptions.value,
              series: chartData.value.series
            }, null, 8, ["options", "series"]))
          : _createCommentVNode("", true)
      ]),
      _cache[1] || (_cache[1] = _createElementVNode("hr", null, null, -1)),
      _createElementVNode("small", null, [
        _createElementVNode("p", {
          innerHTML: _unref(t)('fr.monthly_flow.chart_explanation', 'Vedi quella linea arancione? Sono le tue scadenze fiscali attuali: imprevedibili e stressanti. La linea blu, invece, è il tuo futuro con Smart Rate: una rata mensile costante, zero ansia e piena prevedibilità.<br><br>Il nostro sistema trasforma la complessità in una semplicità disarmante, permettendoti di concentrarti solo sul tuo lavoro. Questa è la serenità finanziaria.')
        }, null, 8, _hoisted_9)
      ])
    ])
  ]))
}
}

})